import { jsx as _jsx } from "react/jsx-runtime";
import { useAuth0 } from '@auth0/auth0-react';
import { CLIENT_ROUTES, StorageKey } from '@client/constants';
import { useApiClient } from '@client/hooks/useApiClient';
import { toastInfo, toastSuccess } from '@client/toaster';
import { callApi } from '@client/utils/apiUtils';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
const FavoritesContext = React.createContext(undefined);
export const FavoritesProvider = (props) => {
    const [favoriteIds, setFavoriteIds] = useState(new Set());
    const [favoriteShopIds, setFavoriteShopIds] = useState(new Set());
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const { isAuthenticated, loginWithRedirect } = useAuth0();
    const apiClient = useApiClient();
    const navigate = useNavigate();
    const processPendingFavorite = async () => {
        const pendingShortId = sessionStorage.getItem(StorageKey.PENDING_FAVORITE);
        if (!pendingShortId)
            return;
        sessionStorage.removeItem(StorageKey.PENDING_FAVORITE);
        await callApi(apiClient.listings.favorite({
            params: { id: pendingShortId },
            body: {},
        }));
    };
    const loadFavorites = async () => {
        setIsLoading(true);
        setError(null);
        await processPendingFavorite();
        const [listingsResult, shopsResult] = await Promise.all([
            callApi(apiClient.me.getFavorites()),
            callApi(apiClient.me.getFavoriteShops()),
        ]);
        if (listingsResult.error !== null ||
            shopsResult.error !== null) {
            setError('Failed to fetch favorites');
        }
        else {
            setFavoriteIds(new Set(listingsResult.data.listings.map((listing) => listing.shortId)));
            setFavoriteShopIds(new Set(shopsResult.data.map((shop) => shop.shortId)));
        }
        setIsLoading(false);
    };
    useEffect(() => {
        if (isAuthenticated) {
            loadFavorites();
        }
        else {
            setFavoriteIds(new Set());
        }
    }, [isAuthenticated]);
    const isFavorited = (shortId) => favoriteIds.has(shortId);
    const isFavoritedShop = (shortId) => favoriteShopIds.has(shortId);
    const toggleFavoriteShop = async (shop) => {
        if (!isAuthenticated) {
            loginWithRedirect({
                appState: { returnTo: window.location.pathname },
            });
            return;
        }
        const wasFavorited = favoriteShopIds.has(shop.shortId);
        setFavoriteShopIds((prev) => {
            const next = new Set(prev);
            if (wasFavorited) {
                next.delete(shop.shortId);
            }
            else {
                next.add(shop.shortId);
            }
            return next;
        });
        const res = wasFavorited
            ? await callApi(apiClient.shops.unfavorite({
                params: { id: shop.shortId },
                body: {},
            }))
            : await callApi(apiClient.shops.favorite({
                params: { id: shop.shortId },
                body: {},
            }));
        if (res.error !== null) {
            setFavoriteShopIds((prev) => {
                const reverted = new Set(prev);
                if (wasFavorited) {
                    reverted.add(shop.shortId);
                }
                else {
                    reverted.delete(shop.shortId);
                }
                return reverted;
            });
        }
        else if (wasFavorited) {
            toastInfo('Removed from favorites', shop.title);
        }
        else {
            toastSuccess('Favorited', shop.title, {
                action: {
                    label: 'View',
                    onClick: () => navigate(`/${CLIENT_ROUTES.favorites}/${CLIENT_ROUTES.shops}`),
                },
            });
        }
    };
    const toggleFavorite = async (listing) => {
        if (!isAuthenticated) {
            sessionStorage.setItem(StorageKey.PENDING_FAVORITE, listing.shortId);
            loginWithRedirect({
                appState: { returnTo: `/${CLIENT_ROUTES.favorites}` },
            });
            return;
        }
        const wasFavorited = favoriteIds.has(listing.shortId);
        setFavoriteIds((prevFavorites) => {
            const newFavorites = new Set(prevFavorites);
            if (wasFavorited) {
                newFavorites.delete(listing.shortId);
            }
            else {
                newFavorites.add(listing.shortId);
            }
            return newFavorites;
        });
        const res = wasFavorited
            ? await callApi(apiClient.listings.unfavorite({
                params: { id: listing.shortId },
                body: {},
            }))
            : await callApi(apiClient.listings.favorite({
                params: { id: listing.shortId },
                body: {},
            }));
        if (res.error !== null) {
            setFavoriteIds((prev) => {
                const reverted = new Set(prev);
                if (wasFavorited) {
                    reverted.add(listing.shortId);
                }
                else {
                    reverted.delete(listing.shortId);
                }
                return reverted;
            });
        }
        else if (wasFavorited) {
            toastInfo('Removed from favorites', listing.title);
        }
        else {
            toastSuccess('Favorited', listing.title, {
                action: {
                    label: 'View',
                    onClick: () => navigate(`/${CLIENT_ROUTES.favorites}/${CLIENT_ROUTES.listings}`),
                },
            });
        }
    };
    return (_jsx(FavoritesContext.Provider, { value: {
            favoriteIds,
            isFavorited,
            toggleFavorite,
            favoriteShopIds,
            isFavoritedShop,
            toggleFavoriteShop,
            isLoading,
            error,
        }, children: props.children }));
};
export const useFavorites = () => {
    const ctx = useContext(FavoritesContext);
    if (!ctx) {
        throw new Error('useFavorites must be used within a FavoritesProvider');
    }
    return ctx;
};
